import React, { useState } from "react";
import { Button, Divider, Input, InputNumber, Modal, Segmented, Space, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGift } from "@fortawesome/free-solid-svg-icons";
import authApi from "../../../api/authApi";
import handleErrorAsync from "../../../utils/handleErrorAsync";
import BulkBonus from "./BulkBonus";
import BonusHistory from "./BonusHistory";

const TAB_OPTIONS = [
    { value: "single", label: "Thưởng 1 User" },
    { value: "bulk",   label: "Thưởng Hàng Loạt" },
];

/** Quick preset amounts in VND */
const QUICK_AMOUNTS = [10_000, 50_000, 100_000, 200_000, 500_000, 1_000_000];

function Bonus() {
    const [tab, setTab] = useState("single");
    const [inputs, setInputs] = useState({ userId: null, bonusCash: null, title: "", note: "" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isLoading, setIsLoading]     = useState(false);

    const setField = (key, val) => setInputs((p) => ({ ...p, [key]: val }));

    const openConfirm = () => {
        if (!inputs.userId || inputs.userId <= 0) return message.error("Vui lòng nhập User ID hợp lệ");
        if (!inputs.bonusCash || inputs.bonusCash <= 0) return message.error("Vui lòng nhập số tiền thưởng hợp lệ");
        setConfirmOpen(true);
    };

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            await authApi.bonusCash({
                userId:    Number(inputs.userId),
                bonusCash: inputs.bonusCash,
                title:     inputs.title || "",
                note:      inputs.note  || "",
            });
            message.success("Thưởng tiền thành công");
            setInputs({ userId: null, bonusCash: null, title: "", note: "" });
            setConfirmOpen(false);
        } catch (error) {
            let msg = "Thất bại";
            handleErrorAsync(error, (m) => { msg = m; });
            message.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-4">
            <h1 className="text-xl font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faGift} className="text-pink-500" />
                Thưởng tiền
            </h1>

            <Segmented options={TAB_OPTIONS} value={tab} onChange={setTab} />

            {tab === "single" ? (
                <div className="bg-surface border border-line rounded-lg p-5 shadow-sm max-w-lg flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">User ID</label>
                        <InputNumber
                            value={inputs.userId}
                            onChange={(v) => setField("userId", v)}
                            placeholder="Nhập User ID"
                            style={{ width: "100%" }}
                            min={1}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Số tiền thưởng</label>
                        <InputNumber
                            value={inputs.bonusCash}
                            onChange={(v) => setField("bonusCash", v)}
                            placeholder="Nhập số tiền"
                            style={{ width: "100%" }}
                            min={1}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            parser={(v) => v?.replace(/,/g, "")}
                            addonAfter="đ"
                        />
                        {/* Quick amount presets */}
                        <Space wrap size={4} className="mt-1">
                            {QUICK_AMOUNTS.map((amt) => (
                                <Button
                                    key={amt}
                                    size="small"
                                    type={inputs.bonusCash === amt ? "primary" : "default"}
                                    onClick={() => setField("bonusCash", amt)}
                                >
                                    {(amt / 1000).toLocaleString()}k
                                </Button>
                            ))}
                        </Space>
                    </div>

                    <Divider className="my-0" />

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Tiêu đề <span className="text-content-3 font-normal">(tuỳ chọn)</span></label>
                        <Input
                            value={inputs.title}
                            onChange={(e) => setField("title", e.target.value)}
                            placeholder="Tiêu đề thông báo"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium">Nội dung <span className="text-content-3 font-normal">(tuỳ chọn)</span></label>
                        <Input.TextArea
                            value={inputs.note}
                            onChange={(e) => setField("note", e.target.value)}
                            placeholder="Nội dung thông báo"
                            rows={3}
                        />
                    </div>

                    <Button type="primary" onClick={openConfirm} block size="large">
                        Xác nhận thưởng
                    </Button>
                </div>
            ) : (
                <BulkBonus />
            )}

            {/* History section — always visible below the form */}
            <Divider orientation="left" className="mt-2 mb-0">
                <span className="text-base font-semibold text-content-2">Lịch sử thưởng</span>
            </Divider>
            <BonusHistory />

            {/* Confirmation modal */}
            <Modal
                open={confirmOpen}
                title="Xác nhận thưởng tiền"
                onOk={onSubmit}
                onCancel={() => setConfirmOpen(false)}
                okText="Xác nhận"
                cancelText="Huỷ"
                okButtonProps={{ loading: isLoading }}
            >
                <div className="flex flex-col gap-2 py-2">
                    <div className="flex justify-between py-1 border-b border-line">
                        <span className="text-content-3">User ID</span>
                        <span className="font-semibold">{inputs.userId}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-line">
                        <span className="text-content-3">Số tiền</span>
                        <span className="font-semibold text-pink-600 text-base">
                            {(inputs.bonusCash || 0).toLocaleString("vi-VN")}đ
                        </span>
                    </div>
                    {inputs.title && (
                        <div className="flex justify-between py-1 border-b border-line">
                            <span className="text-content-3">Tiêu đề</span>
                            <span>{inputs.title}</span>
                        </div>
                    )}
                    {inputs.note && (
                        <div className="flex justify-between gap-4 py-1">
                            <span className="text-content-3 shrink-0">Nội dung</span>
                            <span className="text-right text-content-2">{inputs.note}</span>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}

export default Bonus;
